import { PrescricaoType } from '_metronic/helpers/enums'
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { agruparEmDocumentos, numeracaoDosItens } from '../core/montagem/documentos'
import {
  ValoresItem,
  ItemCatalogo,
  ItemPrescricao,
  criarItem,
  mesmoItemDoCatalogo,
} from '../core/montagem/itens'
import {
  CampoPendente,
  camposPendentes,
  idDoCampo,
  primeiroCampo,
} from '../core/montagem/pendencias'

type Removido = { item: ItemPrescricao; indice: number }

type Estado = {
  itens: ItemPrescricao[]
  /** Card aberto para edição (um por vez). */
  aberto: string | null
  /** Último item removido, para o "Desfazer". */
  removido: Removido | null
}

type Acao =
  | { tipo: 'adicionar'; itens: ItemPrescricao[]; abrir?: string }
  | { tipo: 'atualizar'; chave: string; campos: ValoresItem }
  | { tipo: 'atualizarCatalogo'; chave: string; catalogo: ItemCatalogo }
  | { tipo: 'remover'; chave: string }
  | { tipo: 'desfazer' }
  | { tipo: 'esquecerRemovido' }
  | { tipo: 'alternar'; chave: string }
  | { tipo: 'abrir'; chave: string }
  | { tipo: 'limpar' }

const inicial: Estado = { itens: [], aberto: null, removido: null }

const reducer = (estado: Estado, acao: Acao): Estado => {
  switch (acao.tipo) {
    case 'adicionar':
      return {
        ...estado,
        itens: [...estado.itens, ...acao.itens],
        aberto: acao.abrir ?? estado.aberto,
      }
    case 'atualizar':
      return {
        ...estado,
        itens: estado.itens.map((item) =>
          item.chave === acao.chave ? ({ ...item, ...acao.campos } as ItemPrescricao) : item
        ),
      }
    case 'atualizarCatalogo':
      return {
        ...estado,
        itens: estado.itens.map((item) => {
          if (item.chave !== acao.chave) return item
          // Orientação se edita pelo modal: o texto do item acompanha o texto editado.
          return item.categoria === PrescricaoType.Orientacoes
            ? { ...item, catalogo: acao.catalogo, texto: acao.catalogo.textoPadrao ?? item.texto }
            : { ...item, catalogo: acao.catalogo }
        }),
      }
    case 'remover': {
      const indice = estado.itens.findIndex((item) => item.chave === acao.chave)
      if (indice < 0) return estado
      return {
        itens: estado.itens.filter((item) => item.chave !== acao.chave),
        aberto: estado.aberto === acao.chave ? null : estado.aberto,
        removido: { item: estado.itens[indice], indice },
      }
    }
    case 'desfazer': {
      if (!estado.removido) return estado
      const itens = [...estado.itens]
      itens.splice(estado.removido.indice, 0, estado.removido.item)
      return { ...estado, itens, removido: null }
    }
    case 'esquecerRemovido':
      return { ...estado, removido: null }
    case 'alternar':
      return { ...estado, aberto: estado.aberto === acao.chave ? null : acao.chave }
    case 'abrir':
      return { ...estado, aberto: acao.chave }
    case 'limpar':
      return inicial
  }
}

export type PendenciaItem = {
  chave: string
  campo: CampoPendente
  /** Número de exibição do item; orientações não têm número. */
  numero?: number
  idCampo: string
}

/**
 * Estado da prescrição em montagem: itens, card aberto, desfazer remoção e foco. Deriva daí os
 * documentos previstos e as pendências; nada é gravado no backend até a assinatura.
 */
export const useMontagemPrescricao = () => {
  const [estado, dispatch] = useReducer(reducer, inicial)
  // Itens da hora para quem chama de fora do ciclo de render (ex.: modal do `useModal`, que
  // guarda as props da abertura): o callback continua estável e não compara com lista velha.
  const itensRef = useRef(estado.itens)
  itensRef.current = estado.itens
  const [alvoFoco, setAlvoFoco] = useState<string | null>(null)

  // Foco depois da renderização: o campo só existe quando o card já abriu.
  useEffect(() => {
    if (!alvoFoco) return
    const timer = setTimeout(() => {
      document.getElementById(alvoFoco)?.focus()
      setAlvoFoco(null)
    }, 50)
    return () => clearTimeout(timer)
  }, [alvoFoco])

  const jaAdicionado = useCallback(
    (catalogo: ItemCatalogo) =>
      estado.itens.some((item) => mesmoItemDoCatalogo(item.catalogo, catalogo)),
    [estado.itens]
  )

  /** Adiciona um item novo, abre o card e leva o foco ao primeiro campo. */
  const adicionar = useCallback((catalogo: ItemCatalogo) => {
    const item = criarItem(catalogo)
    dispatch({ tipo: 'adicionar', itens: [item], abrir: item.chave })
    const campo = primeiroCampo(item)
    setAlvoFoco(campo ? idDoCampo(item.chave, campo) : `${item.chave}-cabecalho`)
  }, [])

  /** Adiciona itens já preenchidos (repetir do histórico), ignorando os que já estão na tela. */
  const adicionarVarios = useCallback((novos: ItemPrescricao[]) => {
    const ineditos = novos.filter(
      (novo) => !itensRef.current.some((item) => mesmoItemDoCatalogo(item.catalogo, novo.catalogo))
    )
    dispatch({ tipo: 'adicionar', itens: ineditos })
    // Dois cliques antes do próximo render também não duplicam.
    itensRef.current = [...itensRef.current, ...ineditos]
    return ineditos.length
  }, [])

  const atualizar = useCallback(
    (chave: string, campos: ValoresItem) => dispatch({ tipo: 'atualizar', chave, campos }),
    []
  )
  const atualizarCatalogo = useCallback(
    (chave: string, catalogo: ItemCatalogo) =>
      dispatch({ tipo: 'atualizarCatalogo', chave, catalogo }),
    []
  )
  const remover = useCallback((chave: string) => dispatch({ tipo: 'remover', chave }), [])
  const desfazerRemocao = useCallback(() => dispatch({ tipo: 'desfazer' }), [])
  const esquecerRemovido = useCallback(() => dispatch({ tipo: 'esquecerRemovido' }), [])
  const alternar = useCallback((chave: string) => dispatch({ tipo: 'alternar', chave }), [])
  const limpar = useCallback(() => dispatch({ tipo: 'limpar' }), [])

  /** Abre o card do item e foca o campo (vindo de uma pendência da revisão). */
  const irParaCampo = useCallback((chave: string, idCampo: string) => {
    dispatch({ tipo: 'abrir', chave })
    setAlvoFoco(idCampo)
  }, [])

  const documentos = useMemo(() => agruparEmDocumentos(estado.itens), [estado.itens])
  const numeros = useMemo(() => numeracaoDosItens(documentos), [documentos])

  const pendencias = useMemo<PendenciaItem[]>(
    () =>
      documentos
        .flatMap((documento) => documento.itens)
        .flatMap((item) =>
          camposPendentes(item).map((campo) => ({
            chave: item.chave,
            campo,
            numero: numeros.get(item.chave),
            idCampo: idDoCampo(item.chave, campo),
          }))
        ),
    [documentos, numeros]
  )

  return {
    itens: estado.itens,
    aberto: estado.aberto,
    removido: estado.removido?.item,
    documentos,
    numeros,
    pendencias,
    jaAdicionado,
    adicionar,
    adicionarVarios,
    atualizar,
    atualizarCatalogo,
    remover,
    desfazerRemocao,
    esquecerRemovido,
    alternar,
    irParaCampo,
    limpar,
  }
}
